import { FormInstance, Space } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { RoleType, Team } from 'api/models';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Radio } from 'components/atoms/radio';
import { RadioGroup } from 'components/atoms/radio-buttons';
import { Select } from 'components/atoms/select';
import { Option as BaseOptionT } from 'components/atoms/select/types';
import { useTranslate } from 'hooks/use-translation';

interface Props {
  isCustomerRoleAvailable: boolean;
  form: FormInstance;
  roleOptions: BaseOptionT[];
  placeholders: { [id: number]: Team };
  isEditMode?: boolean;
}

export interface PlaceholderFormValues {
  name: string;
  role: BaseOptionT;
}

export const PlaceholderForm: FC<Props> = ({
  form,
  roleOptions,
  placeholders,
  isEditMode,
  isCustomerRoleAvailable,
}) => {
  const translate = useTranslate();
  const getPlaceholderNames = useCallback(() => {
    return Object.values(placeholders).reduce<string[]>(
      (prev, current) => [...prev, current.teamName],
      [],
    );
  }, [placeholders]);
  const [_selectedType, setSelectedType] = useState<RoleType>(RoleType.Vendor);
  const requiredErrorMessage = translate('input.required');

  const onRoleChange = (selectedRole) => {
    const { label, value } = selectedRole;

    const placeHolderCount = Object.values(placeholders).filter(
      ({ roleId, teamType }) =>
        value !== -1 ? roleId === value : teamType === 'EXTERNAL',
    ).length;

    /** Auto suggest name */
    form.setFieldsValue({ name: `${label} ${placeHolderCount + 1}` });
  };
  const onTypeChange = useCallback(
    (event) => {
      const type = event.target.value;

      setSelectedType(type);
      form.setFieldsValue({ name: null, role: null });
    },
    [form],
  );
  const intendedRoleOptions = useMemo(
    () => roleOptions.filter(({ extra }) => extra?.type === _selectedType),
    [_selectedType, roleOptions],
  );

  return (
    <Form layout="vertical" form={form} autoComplete="off">
      <Form.Item
        name="type"
        required
        label={<FormattedMessage id="type" />}
        hidden={!isCustomerRoleAvailable}
      >
        <RadioGroup disabled={isEditMode} onChange={onTypeChange}>
          <Space direction="vertical">
            <Radio value={RoleType.Vendor}>
              <FormattedMessage id="settings.roles.tabs.vendor" />
            </Radio>
            <Radio value={RoleType.Customer}>
              <FormattedMessage id="settings.roles.tabs.customer" />
            </Radio>
          </Space>
        </RadioGroup>
      </Form.Item>
      <Form.Item
        required
        rules={[{ required: true, message: requiredErrorMessage }]}
        name="role"
        label={<FormattedMessage id="role" />}
      >
        <Select
          isDisabled={isEditMode}
          onChange={onRoleChange}
          options={intendedRoleOptions}
          placeholder={translate(
            'settings.placeholder.form.fields.role.placeholder',
          )}
          value={form.getFieldValue('role')}
        />
      </Form.Item>
      <Form.Item
        required
        rules={[
          { required: true, message: requiredErrorMessage },
          {
            validator: (_, value) => {
              const existing = getPlaceholderNames();
              const formattedValue = (value || '').toLowerCase().trim();

              if (
                existing?.find(
                  (_name) => _name.toLowerCase().trim() === formattedValue,
                )
              ) {
                return Promise.reject(new Error(`${value} already exists`));
              }

              return Promise.resolve();
            },
          },
        ]}
        name="name"
        label={<FormattedMessage id="name" />}
      >
        <Input
          placeholder={translate(
            'settings.placeholder.form.fields.name.placeholder',
          )}
        />
      </Form.Item>
    </Form>
  );
};
